import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Access, type MeResponse } from '@photos/shared';
import { useTags } from '../api/hooks';
import { TagIndex } from '../lib/tags';

export interface Session {
  me: MeResponse;
  user: NonNullable<MeResponse['user']>;
  /** The same permission rules the API enforces. */
  access: Access;
  tagIndex: TagIndex;
  /** e.g. "Media team", shown under the user's name. */
  roleLabel: string;
}

/** "Admin", or the user type: "Media team" or "User". */
export function roleLabelOf(user: Pick<NonNullable<MeResponse['user']>, 'isAdmin' | 'type'>): string {
  if (user.isAdmin) return 'Admin';
  return user.type === 'media' ? 'Media team' : 'User';
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const tags = useTags();
  const value = useMemo<Session>(() => {
    const user = me.user!;
    return {
      me,
      user,
      access: new Access(user, me.collaboratorAlbumIds),
      tagIndex: new TagIndex(tags.data?.tags, tags.data?.aliases),
      roleLabel: roleLabelOf(user),
    };
  }, [me, tags.data]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
